import {
  collection,
  doc,
  addDoc,
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  onSnapshot,
  serverTimestamp,
  writeBatch,
  Timestamp,
} from 'firebase/firestore'
import { db } from '@/config/firebase'
import type { Notification, NotificationType } from '@/types'

const NOTIFICATIONS_COLLECTION = 'notifications'

// Create a notification for a user
export async function createNotification(
  userId: string,
  type: NotificationType,
  title: string,
  message: string,
  data?: Record<string, unknown>
): Promise<string | null> {
  try {
    const notificationData = {
      userId,
      type,
      title,
      message,
      read: false,
      data: data || {},
      createdAt: serverTimestamp(),
    }

    const docRef = await addDoc(collection(db, NOTIFICATIONS_COLLECTION), notificationData)
    return docRef.id
  } catch (error) {
    console.error('Error creating notification:', error)
    return null
  }
}

// Get notifications for a user
export async function getUserNotifications(
  userId: string,
  maxResults: number = 50
): Promise<Notification[]> {
  try {
    const q = query(
      collection(db, NOTIFICATIONS_COLLECTION),
      where('userId', '==', userId),
      orderBy('createdAt', 'desc'),
      limit(maxResults)
    )

    const snapshot = await getDocs(q)
    return snapshot.docs.map((doc) => {
      const data = doc.data()
      return {
        id: doc.id,
        userId: data.userId,
        type: data.type,
        title: data.title,
        message: data.message,
        read: data.read,
        data: data.data,
        createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : new Date(),
      } as Notification
    })
  } catch (error) {
    console.error('Error fetching notifications:', error)
    return []
  }
}

// Get unread count for a user
export async function getUnreadCount(userId: string): Promise<number> {
  try {
    const q = query(
      collection(db, NOTIFICATIONS_COLLECTION),
      where('userId', '==', userId),
      where('read', '==', false)
    )

    const snapshot = await getDocs(q)
    return snapshot.size
  } catch (error) {
    console.error('Error fetching unread count:', error)
    return 0
  }
}

// Mark a notification as read
export async function markAsRead(notificationId: string): Promise<boolean> {
  try {
    await updateDoc(doc(db, NOTIFICATIONS_COLLECTION, notificationId), {
      read: true,
    })
    return true
  } catch (error) {
    console.error('Error marking notification as read:', error)
    return false
  }
}

// Mark all notifications as read for a user
export async function markAllAsRead(userId: string): Promise<boolean> {
  try {
    const q = query(
      collection(db, NOTIFICATIONS_COLLECTION),
      where('userId', '==', userId),
      where('read', '==', false)
    )

    const snapshot = await getDocs(q)

    if (snapshot.empty) return true

    const batch = writeBatch(db)
    snapshot.docs.forEach((docSnap) => {
      batch.update(docSnap.ref, { read: true })
    })

    await batch.commit()
    return true
  } catch (error) {
    console.error('Error marking all as read:', error)
    return false
  }
}

// Subscribe to real-time notifications
export function subscribeToNotifications(
  userId: string,
  callback: (notifications: Notification[]) => void,
  maxResults: number = 20
): () => void {
  const q = query(
    collection(db, NOTIFICATIONS_COLLECTION),
    where('userId', '==', userId),
    orderBy('createdAt', 'desc'),
    limit(maxResults)
  )

  return onSnapshot(q, (snapshot) => {
    const notifications = snapshot.docs.map((doc) => {
      const data = doc.data()
      return {
        id: doc.id,
        userId: data.userId,
        type: data.type,
        title: data.title,
        message: data.message,
        read: data.read,
        data: data.data,
        createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : new Date(),
      } as Notification
    })
    callback(notifications)
  })
}

// Helper functions to create specific notification types

export async function notifyDepositApproved(
  userId: string,
  amount: number
): Promise<string | null> {
  return createNotification(
    userId,
    'deposit_approved',
    '¡Depósito Acreditado!',
    `Tu depósito de $${amount.toLocaleString()} ha sido acreditado a tu cuenta.`,
    { amount }
  )
}

export async function notifyDepositRejected(
  userId: string,
  amount: number,
  reason?: string
): Promise<string | null> {
  return createNotification(
    userId,
    'deposit_rejected',
    'Depósito Rechazado',
    reason || `Tu solicitud de depósito de $${amount.toLocaleString()} ha sido rechazada.`,
    { amount, reason }
  )
}

export async function notifyWithdrawalApproved(
  userId: string,
  amount: number
): Promise<string | null> {
  return createNotification(
    userId,
    'withdrawal_approved',
    '¡Retiro Procesado!',
    `Tu retiro de $${amount.toLocaleString()} ha sido procesado y será acreditado en 24-48hs.`,
    { amount }
  )
}

export async function notifyWithdrawalRejected(
  userId: string,
  amount: number,
  reason?: string
): Promise<string | null> {
  return createNotification(
    userId,
    'withdrawal_rejected',
    'Retiro Rechazado',
    reason || `Tu solicitud de retiro de $${amount.toLocaleString()} ha sido rechazada. El monto fue reintegrado a tu balance.`,
    { amount, reason }
  )
}

export async function notifyKycApproved(userId: string): Promise<string | null> {
  return createNotification(
    userId,
    'kyc_approved',
    '¡Identidad Verificada!',
    'Tu verificación de identidad ha sido aprobada. Ya podés realizar retiros sin restricciones.',
    {}
  )
}

export async function notifyKycRejected(
  userId: string,
  reason?: string
): Promise<string | null> {
  return createNotification(
    userId,
    'kyc_rejected',
    'Verificación Rechazada',
    reason || 'Tu verificación de identidad ha sido rechazada. Por favor, revisá los documentos y volvé a intentarlo.',
    { reason }
  )
}

export async function notifyWelcome(userId: string, name: string): Promise<string | null> {
  return createNotification(
    userId,
    'welcome',
    '¡Bienvenido a Cosa Nostra Casino!',
    `Hola ${name}, tu membresía ha sido aprobada. ¡Buena suerte en las mesas!`,
    {}
  )
}
